import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { DestinationTeamSection } from "@ckgo2/shared/types";
import { Plus, Search, Users, Edit, Trash2, MapPin } from "lucide-react";

interface TeamWithDest extends DestinationTeamSection {
  destinationName?: string;
}

const SECTION_LABELS: Record<string, string> = {
  guarantor: "Guarantor",
  sales_team: "Sales Team",
  guide: "Guide",
  local_team: "Local Team",
};

const SECTION_COLORS: Record<string, string> = {
  guarantor: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  sales_team: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  guide: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  local_team: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

export default function DestinationTeamsPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["destination-teams"],
    queryFn: () => api.get<ApiResponse<TeamWithDest[]>>("/api/admin/destination-teams"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/destination-teams/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["destination-teams"] });
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const teams = data?.data || [];

  const filteredTeams = teams.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.destinationName?.toLowerCase().includes(search.toLowerCase());
    const matchesType = !typeFilter || t.sectionType === typeFilter;
    return matchesSearch && matchesType;
  });

  if (error) {
    return (
      <div className="card text-error-600 text-center">
        Error loading destination teams. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Destination Teams</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Manage team sections for each destination
          </p>
        </div>
        <Link to="/destination-teams/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          New team section
        </Link>
      </div>

      {/* Filters */}
      <div className="card flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or destination..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="input w-full sm:w-48"
        >
          <option value="">All types</option>
          {Object.entries(SECTION_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
          <Users className="mb-2 h-12 w-12 text-gray-300" />
          <p>No team sections found</p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">
                  Destination
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">
                  Order
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredTeams.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {team.photo ? (
                        <img
                          src={team.photo}
                          alt={team.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                          <Users className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{team.name}</p>
                        {team.subtitle && <p className="text-sm text-gray-500">{team.subtitle}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${SECTION_COLORS[team.sectionType] || ""}`}
                    >
                      {SECTION_LABELS[team.sectionType] || team.sectionType}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {team.destinationName && (
                      <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                        <MapPin className="h-3.5 w-3.5" />
                        {team.destinationName}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{team.sortOrder}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/destination-teams/${team.id}`} className="btn-ghost btn-sm">
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(team.id, team.name)}
                        disabled={deleteMutation.isPending}
                        className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
